import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, money, EASE, imgPos } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { MixPicker, mixText } from "@/components/MixPicker";
import { SeasonalTag } from "@/components/SeasonalTag";

const Gallery = ({ p, focus }) => {
  const [img, setImg] = useState(0);
  const imgs = p.images ?? [];
  useEffect(() => {
    const i = focus ? imgs.indexOf(focus) : -1;
    if (i >= 0) setImg(i);
  }, [focus, imgs]);
  return (
    <div className="lg:sticky lg:top-28">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#F1EDE4]">
        <AnimatePresence initial={false}>
          <motion.img key={imgs[img]} src={imgs[img]} alt={p.name} data-testid="product-main-image"
            initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: EASE }} className="absolute inset-0 w-full h-full object-cover" />
        </AnimatePresence>
      </div>
      {imgs.length > 1 && (
        <div className="flex flex-wrap gap-3 mt-3">
          {imgs.map((s, i) => (
            <button key={s} data-testid={`product-thumb-${i}`} onClick={() => setImg(i)} className={`w-20 h-24 overflow-hidden border transition-[opacity,border-color] ${i === img ? "border-espresso" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={s} alt="" className={`w-full h-full object-cover ${imgPos(s)}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Choice = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`px-5 py-3 border text-sm transition-colors duration-300 ${active ? "bg-espresso text-cream border-espresso" : "border-espresso/25 hover:border-espresso"}`}>
    {children}
  </button>
);

const Buy = ({ p, onFocus }) => {
  const { add } = useCart();
  const [opt, setOpt] = useState(p.options?.[0]);
  const [flavor, setFlavorState] = useState(p.flavors?.find((f) => p.flavor_details?.[f]) || p.flavors?.[0] || "");
  const detail = p.flavor_details?.[flavor];
  const setFlavor = (f) => { setFlavorState(f); onFocus(p.flavor_details?.[f]?.img); };
  useEffect(() => {
    if (!p.mix_and_match || p.options?.[0]?.id === "single") onFocus(p.flavor_details?.[flavor]?.img);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [qty, setQty] = useState(1);
  const [mix, setMix] = useState({});
  const boxSize = p.mix_and_match ? opt?.box_size : null;
  const mixTotal = Object.values(mix).reduce((s, n) => s + n, 0);
  const ready = !boxSize || mixTotal === boxSize;
  const pickSize = (o) => { setOpt(o); setMix({}); };
  const addToBag = () => {
    const mixClean = boxSize ? Object.fromEntries(Object.entries(mix).filter(([, n]) => n > 0)) : null;
    add({ lookup_key: opt.lookup_key, slug: p.slug, name: p.name, option: opt.label, flavor: boxSize ? mixText(mixClean) : flavor, mix: mixClean, price: opt.price, qty, image: p.images?.[0] });
    if (boxSize) setMix({});
  };
  if (!p.purchasable)
    return (
      <div className="mt-10">
        <p className="text-mocha leading-relaxed">Tell us how many cookies and which flavours you’d like, and we’ll send a personal quote.</p>
        <Link to="/custom-orders?item=Cookie%20Box" data-testid="request-cookie-box" className="btn btn-dark mt-6 w-full">Request a Cookie Box</Link>
      </div>
    );
  return (
    <div className="mt-10 space-y-8">
      <p data-testid="selected-price" className="font-display text-5xl">{money(opt.price)}<span className="text-base font-sans text-mocha ml-3">{opt.label}</span></p>
      {p.options.length > 1 && (
        <div><p className="eyebrow mb-3">Size</p>
          <div className="flex flex-wrap gap-2">{p.options.map((o) => <Choice key={o.id} testId={`option-${o.id}`} active={opt?.id === o.id} onClick={() => pickSize(o)}>{o.label} · {money(o.price)}</Choice>)}</div>
        </div>
      )}
      {boxSize ? (
        <MixPicker flavors={p.flavors} details={p.flavor_details} seasonal={p.seasonal_flavors} size={boxSize} mix={mix} setMix={setMix} onFocus={onFocus} />
      ) : p.flavors?.length > 0 && (
        <div><p className="eyebrow mb-3">Flavour</p>
          <div className="flex flex-wrap gap-2">{p.flavors.map((f) => <Choice key={f} testId={`flavor-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} active={flavor === f} onClick={() => setFlavor(f)}>{f}{p.seasonal_flavors?.includes(f) && <SeasonalTag className="ml-2 align-middle" />}</Choice>)}</div>
          {detail?.desc && <p data-testid="flavor-description" className="mt-4 font-display italic text-xl text-cocoa leading-snug">{detail.desc}</p>}
        </div>
      )}
      <div className="flex gap-3">
        <div className="flex items-center border border-espresso/25">
          <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 sm:p-4" aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
          <span data-testid="qty-value" className="w-8 sm:w-10 text-center">{qty}</span>
          <button data-testid="qty-increase" onClick={() => setQty(Math.min(100, qty + 1))} className="p-3 sm:p-4" aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
        </div>
        <button data-testid="add-to-cart-button" className="btn btn-dark flex-1 min-w-0 px-3 text-[11px] sm:text-[12px] tracking-[0.1em] sm:tracking-[0.24em]" disabled={!ready} onClick={addToBag}>
          {ready ? `Add to Bag · ${money(opt.price * qty)}` : `Choose ${boxSize - mixTotal} more`}
        </button>
      </div>
    </div>
  );
};

export default function Product() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    setP(null);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <main className="pt-44 pb-32 px-5 md:px-10 max-w-[1440px] mx-auto"><p className="font-display text-4xl">This dessert isn’t on the menu.</p><Link to="/shop" className="btn btn-line mt-8">Back to Shop</Link></main>;
  if (!p) return <main className="min-h-screen" />;

  return (
    <main data-testid="product-page" className="mx-auto max-w-[1440px] px-5 md:px-10 pt-32 md:pt-40 pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-7"><Gallery p={p} focus={focus} /></div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }} className="lg:col-span-5">
        <Link to="/shop" data-testid="breadcrumb-shop" className="eyebrow link-u pb-1">Shop</Link><span className="eyebrow"> / {p.category}</span>
        {p.seasonal && <div className="mt-4"><SeasonalTag testId="product-seasonal-badge" className="border border-gold/50 px-3 py-1.5" /><span className="ml-3 text-xs text-mocha">Available for a limited time</span></div>}
        <h1 data-testid="product-name" className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6">{p.name}</h1>
        <p className="font-display italic text-2xl text-mocha mt-3">{p.tagline}</p>
        {!p.purchasable && <p data-testid="product-price" className="mt-8 text-2xl">{p.price_note}</p>}
        <p className="mt-6 text-mocha leading-relaxed">{p.description}</p>
        {p.includes?.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">{p.includes.map((x) => <li key={x} className="font-display text-xl flex items-center gap-2"><span className="w-1.5 h-1.5 rotate-45 bg-gold" />{x}</li>)}</ul>
        )}
        <Buy p={p} onFocus={(src) => src && setFocus(src)} />
        <div data-testid="pickup-info" className="mt-10 p-6 bg-linen flex gap-4">
          <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.4} />
          <div><p className="font-display text-xl">Pickup in Clifton Heights, PA</p><p className="text-sm text-mocha mt-1 leading-relaxed">All orders are pickup only. Pickup details are sent once payment is received.</p></div>
        </div>
        <Accordion type="single" collapsible className="mt-8">
          <AccordionItem value="order"><AccordionTrigger data-testid="accordion-ordering" className="font-display text-xl font-normal">Ordering</AccordionTrigger>
            <AccordionContent className="text-mocha leading-relaxed">Every order is baked to order. Add a preferred pickup date or gift message in your bag notes at checkout. For larger quantities or celebrations, request a custom order.</AccordionContent></AccordionItem>
          <AccordionItem value="care"><AccordionTrigger data-testid="accordion-care" className="font-display text-xl font-normal">Storage & Care</AccordionTrigger>
            <AccordionContent className="text-mocha leading-relaxed">Best enjoyed within a few days. Keep in a cool, dry place away from direct sunlight.</AccordionContent></AccordionItem>
        </Accordion>
      </motion.div>
    </main>
  );
}
